/**
 * Single source of truth for mapping domain statuses (Job, Candidate, File, Role, Plan, Invoice)
 * to <StatusPill> variants. Used by every list, card, table, and detail page so a status looks
 * identical wherever it appears.
 */
import type { StatusVariant } from './StatusPill';

/* ---------- Job statuses (DRAFT / ANALYZING / ACTIVE / INTERVIEWING / CLOSED) ---------- */

export const getJobStatusVariant = (status: string | undefined | null): StatusVariant => {
  switch ((status ?? '').toUpperCase()) {
    case 'DRAFT':
    case 'CLOSED':
    case 'ARCHIVED':
      return 'neutral';
    case 'ANALYZING':
      return 'processing';
    case 'ACTIVE':
      return 'success';
    case 'INTERVIEWING':
      return 'info';
    default:
      return 'neutral';
  }
};

/* ---------- Candidate statuses (PENDING / INTERVIEWING / INTERVIEWED / ACCEPTED / REJECTED / HIRED) ---------- */

export const getCandidateStatusVariant = (status: string | undefined | null): StatusVariant => {
  switch ((status ?? '').toLowerCase()) {
    case 'pending':
      return 'neutral';
    case 'shortlisted':
      return 'warning';
    case 'interviewing':
      return 'processing';
    case 'interviewed':
      return 'info';
    case 'accepted':
      return 'success';
    case 'hired':
      return 'accent';
    case 'rejected':
      return 'error';
    default:
      return 'neutral';
  }
};

/* ---------- File processing (uploading / analyzing / success / failed) ---------- */

export const getFileStatusVariant = (status: string | undefined | null): StatusVariant => {
  switch ((status ?? '').toLowerCase()) {
    case 'uploading':
    case 'queued':
      return 'processing';
    case 'analyzing':
    case 'processing':
      return 'processing';
    case 'success':
    case 'completed':
    case 'done':
      return 'success';
    case 'failed':
    case 'error':
      return 'error';
    default:
      return 'neutral';
  }
};

/* ---------- Workspace member role (OWNER / ADMIN / MEMBER) ---------- */

export const getRoleVariant = (role: string | undefined | null): StatusVariant => {
  switch ((role ?? '').toUpperCase()) {
    case 'OWNER':
      return 'accent';
    case 'ADMIN':
      return 'success';
    case 'MEMBER':
      return 'neutral';
    default:
      return 'neutral';
  }
};

/* ---------- Plan / Invoice ---------- */

export const getPlanStatusVariant = (expired: boolean | undefined): StatusVariant =>
  expired ? 'error' : 'success';

export const getInvoiceStatusVariant = (status: string | undefined | null): StatusVariant => {
  switch ((status ?? '').toLowerCase()) {
    case 'paid':
      return 'success';
    case 'pending':
    case 'processing':
      return 'warning';
    case 'failed':
    case 'overdue':
      return 'error';
    default:
      return 'neutral';
  }
};

/* ---------- Interview statuses (SCHEDULED / IN_PROGRESS / COMPLETED / CANCELLED) ---------- */

export const getInterviewStatusVariant = (status: string | undefined | null): StatusVariant => {
  switch ((status ?? '').toLowerCase()) {
    case 'scheduled':
      return 'info';
    case 'in_progress':
      return 'accent';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'error';
    default:
      return 'neutral';
  }
};
